<script setup>
import { computed } from 'vue'
import { guides } from '@/guides'

// Initialize all guide composables at setup time (required for useRouter/useRoute)
const guideInstances = {}
for (const g of guides) {
  guideInstances[g.id] = g.useGuide()
}

const categories = computed(() => {
  const map = {}
  for (const g of guides) {
    if (!map[g.category]) map[g.category] = []
    map[g.category].push(g)
  }
  return map
})

function getProgress(id) {
  try {
    const progress = JSON.parse(localStorage.getItem('guide_progress') || '{}')
    return progress[id] || null
  } catch {
    return null
  }
}

function startGuide(guide) {
  guideInstances[guide.id].start()
}
</script>

<template>
  <div class="container guides-page">
    <div class="page-container">
      <div class="page-header">
        <div class="header-left">
          <h1 class="page-title">Guides</h1>
          <div class="header-subtitle">Interactive walkthroughs to help you get the most out of the platform.</div>
        </div>
      </div>

      <div v-for="(items, category) in categories" :key="category" class="category-section">
        <div class="category-label">{{ category }}</div>
        <div class="guides-grid">
          <div
            v-for="guide in items"
            :key="guide.id"
            class="guide-card"
            @click="startGuide(guide)"
          >
            <div class="guide-card-icon">
              <font-awesome-icon :icon="['fas', guide.icon]" />
            </div>
            <div class="guide-card-body">
              <div class="guide-card-title">
                {{ guide.title }}
                <span v-if="getProgress(guide.id)" class="guide-completed-badge">
                  <font-awesome-icon :icon="['fas', 'circle-check']" />
                  Completed
                </span>
              </div>
              <div class="guide-card-desc">{{ guide.description }}</div>
            </div>
            <div class="guide-card-action">
              <font-awesome-icon :icon="['fas', 'play']" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.container {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.page-container {
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #f3f4f6;
  margin: 0;
}

.header-subtitle {
  color: #6D7785;
  font-size: 0.875rem;
  margin-top: 0.35rem;
}

.category-section {
  margin-bottom: 2rem;
}

.category-label {
  color: #9CA3AF;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.guides-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.guide-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: rgba(26, 29, 36, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.guide-card:hover {
  background: rgba(26, 29, 36, 0.9);
  border-color: rgba(115, 87, 210, 0.25);
}

.guide-card-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.625rem;
  background: rgba(115, 87, 210, 0.12);
  color: #a78bfa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.guide-card-body {
  flex: 1;
  min-width: 0;
}

.guide-card-title {
  color: #f3f4f6;
  font-size: 0.9375rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.guide-card-desc {
  color: #6D7785;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
  line-height: 1.4;
}

.guide-completed-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  color: #34d399;
  font-weight: 500;
  background: rgba(52, 211, 153, 0.1);
  padding: 0.15rem 0.5rem;
  border-radius: 30px;
}

.guide-card-action {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  color: #6D7785;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
  transition: all 0.2s;
}

.guide-card:hover .guide-card-action {
  background: rgba(115, 87, 210, 0.15);
  color: #a78bfa;
}
</style>
